import { type ReadonlyVec2, type vec2 } from 'gl-matrix';

import { getCubicBezierBoundingBox } from '@canvas/bezier';
import { aPersonCanUseWhiteboards } from '@canvas/capability-summaries';
import { boxCreate, boxExpand, boxExpandToFitPoint } from '@canvas/math/box';
import type { Box } from '@canvas/math/box';
import {
  CustomerImpact,
  EventClassification,
  captureMessageForCapability,
} from '@canvas/sentry-react';

import { walkPathSegments, walkPathSegmentsWithEndCaps } from './path-traversal.ts';
import { createCubicBezierFromSegment } from './path.ts';
import { type PathSegment, type PathStyleForLegacyCaps } from './types.ts';

/**
 * Computes the rectangular bounding box for a list of segments.
 * @param start - the path start point
 * @param segments - the segments of the path
 * @param thickness - the thickness of the path, defaults to 0
 * @param legacyCapsStyleConfig - optional, provide if legacy caps should be included in the bounding box
 * @returns
 */
export const getBoundingBoxForPathSegments = (
  start: ReadonlyVec2,
  segments: PathSegment[],
  thickness = 0,
  legacyCapsStyleConfig?: PathStyleForLegacyCaps,
): Box => {
  let containedNaN = false;
  if (isNaN(start[0]) || isNaN(start[1])) {
    containedNaN = true;
  }
  const box = boxCreate(start[0] || 0, start[0] || 0, start[1] || 0, start[1] || 0);

  const callback = (start: vec2, end: vec2, segment: PathSegment) => {
    if (isNaN(start[0]) || isNaN(start[1]) || isNaN(end[0]) || isNaN(end[1])) {
      containedNaN = true;
    }
    if (segment.type === 'cubic-bezier') {
      const bezier = createCubicBezierFromSegment(segment, start);
      getCubicBezierBoundingBox(bezier, box);
    } else {
      boxExpandToFitPoint(box, end);
    }
  };

  if (legacyCapsStyleConfig) {
    walkPathSegmentsWithEndCaps(start, segments, legacyCapsStyleConfig, callback);
  } else {
    walkPathSegments(start, segments, callback);
  }

  if (containedNaN) {
    captureMessageForCapability(
      'Y Path Element Data contained NaN values',
      {
        tags: {
          captureLocation: 'positionAndSizeToBox-NaN',
          /**
           * This will likely stop paths from being viewable
           */
          customerImpact: CustomerImpact.CUSTOMERS,
          /**
           * Clear break in contract of this function
           */
          eventClassification: EventClassification.BUG,
        },
      },
      aPersonCanUseWhiteboards,
    );
  }

  return thickness === 0 ? box : boxExpand(box, thickness / 2);
};
